import { Button, Flex, Modal, sendNotification, Typography } from 'components'
import React from 'react'
import { OTP } from './OTP'
import { useDeactivateMFAMutation } from 'services/auth/mfa'
import { useDispatch } from 'react-redux'
import { disableMFA } from 'services/slices/auth'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'utils/useTranslation'
import { MFAError } from './ActivateMFAModal'

const schema = z.object({
  otp: z.string().min(6),
})

type FormInputs = z.infer<typeof schema>

export const DeactivateMFAModal: React.FC<{
  isOpen: boolean
  onClose: () => void
}> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('pages.mfa')
  const dispatch = useDispatch()

  const methods = useForm<FormInputs>({
    resolver: zodResolver(schema),
  })

  const close = () => {
    methods.reset()
    onClose()
  }

  const [deactivateMFA] = useDeactivateMFAMutation()

  const onSubmit = (data: FormInputs) => {
    deactivateMFA({ type: 'totp', value: data.otp })
      .unwrap()
      .then(() => {
        sendNotification({
          type: 'success',
          toastContent: t('mfa_deactivated'),
        })
        dispatch(disableMFA())
        close()
      })
      .catch((error) => {
        const errorCode = (error as any)?.data?.error_code
        if (Object.values(MFAError).includes(errorCode as MFAError)) {
          sendNotification({
            type: 'error',
            toastContent: t(`errors.${errorCode}`),
          })
        } else {
          sendNotification({
            type: 'error',
            toastContent: t('errors.something_went_wrong'),
          })
        }
      })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      componentSize="small"
      onRequestClose={close}
      heading={t('deactivate_mfa')}
      noFooter
    >
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Flex
          directionColumn="zero"
          gap={2}
          middle="zero"
          style={{
            overflow: 'hidden',
          }}
        >
          <Typography>{t('deactivate_description')}</Typography>
          <Controller
            control={methods.control}
            name="otp"
            render={({ field: { onChange, value } }) => <OTP onChange={onChange} value={value} />}
          />
          <Button type="submit" color="danger" style={{ width: '100%' }}>
            {t('deactivate_mfa')}
          </Button>
        </Flex>
      </form>
    </Modal>
  )
}
